// src/components/3D/Ground.jsx
import React, { useMemo } from 'react';
import * as THREE from 'three';
import PropTypes from 'prop-types';

const createGroundTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 512; // Increased resolution
  canvas.height = 512;
  const ctx = canvas.getContext('2d');

  // Base color
  ctx.fillStyle = '#a3a3a3';
  ctx.fillRect(0, 0, 512, 512);

  // Add subtle gradient
  const gradient = ctx.createLinearGradient(0, 0, 512, 512);
  gradient.addColorStop(0, '#a8a8a8');
  gradient.addColorStop(1, '#989898');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 512, 512);

  // Add noise texture
  for (let i = 0; i < 20000; i++) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const value = Math.random() * 30;
    ctx.fillStyle = `rgba(0,0,0,${value/100})`;
    ctx.fillRect(x, y, 2, 2);
  }

  // Add grid pattern
  ctx.strokeStyle = 'rgba(255,255,255,0.1)';
  ctx.lineWidth = 1;
  const gridSize = 32;
  
  for (let x = 0; x < 512; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, 512);
    ctx.stroke();
  }
  
  for (let y = 0; y < 512; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(512, y);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1, 1);
  return texture;
};

const Ground = React.memo(({ dimensions }) => {
  const groundTexture = useMemo(() => createGroundTexture(), []);
  
  // Calculate dimensions with safety margin
  const scaleFactor = 1.2;
  const width = dimensions.width * scaleFactor;
  const height = dimensions.height * scaleFactor;
  
  // Adjust texture repeat based on ground size
  groundTexture.repeat.set(Math.ceil(width/400), Math.ceil(height/400));

  // Create ground materials
  const groundMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: groundTexture,
      roughness: 0.8,
      metalness: 0.1,
      color: '#a3a3a3',
      // Add subtle ambient occlusion
      aoMapIntensity: 1.0,
      // Enable shadows
      receiveShadow: true,
    });
  }, [groundTexture]);

  // Calculate grid position to center the ground
  const position = useMemo(() => {
    return [
      -(width/80), // Adjust X to center
      -0.1,        // Slightly below zero to prevent z-fighting
      -(height/80) // Adjust Z to center
    ];
  }, [width, height]);

  return (
    <group>
      {/* Main Ground Plane */}
      <mesh 
        rotation={[-Math.PI/2, 0, 0]} 
        position={position} 
        receiveShadow
      >
        <planeGeometry 
          args={[width/40, height/40, 32, 32]} // Added segments for better shadows
        />
        <meshStandardMaterial 
          {...groundMaterial} 
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Optional: Add a subtle shadow plane slightly above the ground */}
      <mesh
        rotation={[-Math.PI/2, 0, 0]}
        position={[position[0], position[1] + 0.01, position[2]]}
        receiveShadow
      >
        <planeGeometry args={[width/40, height/40]} />
        <meshStandardMaterial
          color="#000000"
          transparent
          opacity={0.1}
          depthWrite={false}
        />
      </mesh>

      {/* Optional: Add grid lines for better visual reference */}
      <gridHelper 
        args={[
          Math.max(width, height)/40, 
          Math.max(width, height)/40, 
          'rgba(255,255,255,0.2)',
          'rgba(150,150,150,0.1)'
        ]} 
        position={[0, 0.01, 0]}
      />
    </group>
  );
});

Ground.propTypes = {
  dimensions: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired
};

export default Ground;